import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { App, Col, DatePicker, Divider, Form, Row, Tooltip, Typography } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs, { Dayjs } from 'dayjs'

import Api from '@vacationtracker/shared/services/api'
import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'
import { notificationStore } from '../../context/notificationsContext/store'
import { ThemeContext } from '../../context/themeContext'
import { getTeamCalendarPalette } from '../team-calendar-grid/colors'
import { Banner } from '../../design-system/Banner'
import { Button } from '../../design-system/Button'
import { Field, FieldControlProps, FieldGroup } from '../../design-system/Field'
import { SegmentedToggle } from '../../design-system/SegmentedToggle'
import { SelectField } from '../../design-system/SelectField'
import { Stack } from '../../design-system/Stack'
import { Text } from '../../design-system/Text'
import { TextField } from '../../design-system/TextField'
import calendarHelper from '../months'
import { HolidayLength, datePickable } from '../location-holidays/logic'
import { DaySet, MIN_SPAN_DAYS, RecurrenceSummary, RecurringType, daySetOf, spanDays } from '../location-holidays/recurrence'
import { useRecurrenceText } from '../location-holidays/RecurrenceEditor'
import { IHolidaysByYear } from '../../types/locations'
import {
  FIRST_PICKABLE_DAY,
  HolidayFormErrors,
  HolidayFormValues,
  findHoliday,
  formErrors,
  formLocks,
  holidayEventBody,
  initialFormValues,
  lastPickableDay,
  overlappingWith,
  pickerOpeningDay,
  takenDays,
} from './payload'

const { Title, Paragraph } = Typography

interface ILocationHolidayFormProps {
  locationId: string
  locationName: string
  workWeek: number[]
  defaultWorkingHours: boolean
  existingLocationHolidays: IHolidaysByYear[]
  existingHolidayId?: string
}

// The Create Location form's section grid.
const SECTION_HEAD = { xxl: 8, xl: 8, lg: 6, md: 6, sm: 24, xs: 24 } as const
const SECTION_BODY = { xxl: 16, xl: 16, lg: 18, md: 18, sm: 24, xs: 24 } as const
const GUTTER = { xs: 8, sm: 16, md: 24, lg: 32 } as const

const DATE_FORMAT = ['dddd, MMMM D, YYYY', 'D MMM YYYY', 'YYYY-MM-DD']
const DAY = 'YYYY-MM-DD'
// Navigating back right away would show the list before the holiday lands.
const BACK_DELAY_MS = 2000

const LocationHolidayForm = ({ locationId, defaultWorkingHours, existingLocationHolidays, existingHolidayId }: ILocationHolidayFormProps) => {
  const history = useHistory()
  const { state: fromTab } = useLocation<{ year?: number } | undefined>()
  const openingDay = useMemo(() => pickerOpeningDay(fromTab?.year), [fromTab?.year])
  const { formatMessage, formatList } = useIntl()
  const { notification } = App.useApp()
  const toText = useRecurrenceText()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  // The team calendar's holiday banner, so a holiday reads the same in both calendars.
  const palette = getTeamCalendarPalette(useContext(ThemeContext).theme === 'dark')
  const [btnLoading, setBtnLoading] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const backTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(backTimer.current), [])

  const existing = useMemo(() => findHoliday(existingLocationHolidays, existingHolidayId), [existingLocationHolidays, existingHolidayId])
  const locks = useMemo(() => formLocks(existing, Boolean(existingHolidayId)), [existing, existingHolidayId])
  const taken = useMemo(() => takenDays(existingLocationHolidays, existing), [existingLocationHolidays, existing])
  const [values, setValues] = useState<HolidayFormValues>(() => initialFormValues(existing, existingLocationHolidays))
  const errors: HolidayFormErrors = showErrors ? formErrors(values) : {}
  const error = (key: keyof HolidayFormErrors) => {
    const id = errors[key]
    return id ? formatMessage({ id }) : undefined
  }

  const multiDay = values.length === 'MULTI_DAY'
  const dynamic = values.recurringType === 'SPECIFIC_DAY'
  const showRepeatDetails = values.recurring && !locks.repeatDetails

  const set = (patch: Partial<HolidayFormValues>) => setValues((current) => ({ ...current, ...patch }))
  // A dynamic rule starts from the picked dates: their weekday and week of the month, and their length.
  const setDates = (patch: Partial<HolidayFormValues>) =>
    setValues((current) => {
      const next = { ...current, ...patch }
      if (!next.startDate) return next
      const duration = next.length === 'MULTI_DAY' && next.endDate ? spanDays({ date: next.startDate, endDate: next.endDate }) : MIN_SPAN_DAYS
      return { ...next, duration, ...('length' in patch ? {} : { daySet: daySetOf(dayjs(next.startDate)) }) }
    })

  const backToHolidays = () => {
    const year = values.startDate ? dayjs(values.startDate).year() : openingDay.year()
    history.push(`/app/settings/locations/${locationId}/holidays`, { year })
  }

  const onFinish = async () => {
    if (locks.whole) return
    setShowErrors(true)
    if (Object.keys(formErrors(values)).length) return

    const body = holidayEventBody({ locationId, values, existing })
    const overlapping = overlappingWith({ body, existing, holidays: existingLocationHolidays })
    if (overlapping) {
      notification.error({
        message: formatMessage(
          { id: 'components.createHolidaysForm.createHolidayErrorOverlap' },
          { holidayName: overlapping.name, holidayDate: new Date(`${holidayDay(overlapping.date)}T00:00:00`) }
        ),
        duration: 0,
      })
      return
    }

    try {
      setBtnLoading(true)
      const response = await Api.post('/core/event', body)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: existing ? 'components.createHolidaysForm.updateInProgress' : 'components.createHolidaysForm.createInProgress' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])
      backTimer.current = setTimeout(backToHolidays, BACK_DELAY_MS)
    } catch (err) {
      setBtnLoading(false)
      notification.error({
        message: formatMessage({ id: 'components.createHolidaysForm.createHolidayError' }),
        description: (err as Error).message || formatMessage({ id: 'error.generic' }),
        duration: 0,
      })
    }
  }

  const yesNo = [
    { label: formatMessage({ id: 'app.yes' }), value: true },
    { label: formatMessage({ id: 'app.no' }), value: false },
  ]
  // Kept for a half-day holiday being edited, so its own length still shows when the hours have changed since.
  const halfDayAvailable = defaultWorkingHours || existing?.isHalfDay === true
  const lengthOptions = [
    { value: 'SINGLE_DAY', label: formatMessage({ id: 'components.createHolidaysForm.singleDayHoliday' }) },
    { value: 'MULTI_DAY', label: formatMessage({ id: 'components.createHolidaysForm.multiDayHoliday' }) },
    ...(halfDayAvailable ? [{ value: 'HALF_DAY', label: formatMessage({ id: 'components.createHolidaysForm.halfDayHoliday' }) }] : []),
  ]
  const toOptions = (entries: { value: number; label: string }[]) => entries.map((entry) => ({ value: entry.value, label: formatMessage({ id: entry.label }) }))

  const lastDay = lastPickableDay()
  const datePicker = (key: 'startDate' | 'endDate', label: string, minDate: Dayjs) => (
    <Field label={label} requirement="required" error={error(key)}>
      {(control: FieldControlProps) => (
        <DatePicker
          {...control}
          value={values[key] ? dayjs(values[key]) : null}
          // Only while empty: antd prefers this over `value`, which would open a picked or edited date elsewhere.
          defaultPickerValue={values[key] ? undefined : key === 'endDate' && values.startDate ? dayjs(values.startDate) : openingDay}
          onChange={(picked: Dayjs | null) => {
            const day = picked ? picked.format(DAY) : undefined
            setDates(key === 'startDate' ? { startDate: day } : { endDate: day })
          }}
          format={DATE_FORMAT}
          disabledDate={(day) =>
            day.isBefore(minDate, 'day') || day.isAfter(lastDay, 'day') || (existing !== undefined && !datePickable(existing, day)) || taken.has(day.format(DAY))
          }
          cellRender={(current, info) => {
            const names = info.type === 'date' && dayjs.isDayjs(current) ? taken.get(current.format(DAY)) : undefined
            if (!names || !React.isValidElement<{ style?: React.CSSProperties }>(info.originNode)) return info.originNode
            return (
              <Tooltip title={formatList(names)}>
                {React.cloneElement(info.originNode, {
                  style: { ...info.originNode.props.style, background: palette.holidayBannerBg, boxShadow: `inset 0 0 0 1px ${palette.holidayBannerBorder}` },
                })}
              </Tooltip>
            )
          }}
          disabled={locks.whole || locks.dates}
          style={{ width: '100%' }}
        />
      )}
    </Field>
  )

  const daySetPart = (part: 0 | 1 | 2, options: { value: number; label: string }[], labelId: string) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <SelectField
        ariaLabel={formatMessage({ id: labelId })}
        size="large"
        searchable={false}
        options={options}
        value={values.daySet[part]}
        onChange={(next) => {
          const daySet = [...values.daySet] as DaySet
          daySet[part] = Number(next)
          set({ daySet })
        }}
      />
    </div>
  )

  const summary: RecurrenceSummary | null = !values.startDate
    ? null
    : dynamic
      ? { kind: 'dynamic', week: values.daySet[0], weekday: values.daySet[1], month: values.daySet[2], transfer: values.transfer }
      : { kind: 'fixed', date: values.startDate, ...(multiDay && values.endDate ? { endDate: values.endDate } : {}), transfer: values.transfer }

  const section = (title: string, description: string, body: React.ReactNode) => (
    <Row gutter={GUTTER}>
      <Col {...SECTION_HEAD}>
        <Title level={4}>{title}</Title>
        <Paragraph type="secondary">{description}</Paragraph>
      </Col>
      <Col {...SECTION_BODY}>
        <Stack gap="lg">{body}</Stack>
      </Col>
    </Row>
  )

  return (
    <div className="form-layout">
      <Form name="holidayForm" layout="vertical" requiredMark="optional" size="large" disabled={locks.whole} onFinish={() => void onFinish()}>
        {section(
          formatMessage({ id: 'app.general' }),
          formatMessage({ id: 'holidaysTab.form.holidayDescription' }),
          <>
            <TextField
              label={formatMessage({ id: 'app.name' })}
              requirement="required"
              size="large"
              placeholder={formatMessage({ id: 'components.createHolidaysForm.name' })}
              value={values.name}
              onChange={(next) => set({ name: String(next ?? '') })}
              error={error('name')}
            />
            {existing?.type === 'LIB' && <Banner tone="warning" message={formatMessage({ id: 'components.createHolidaysForm.holidayFormRecurrenceDisabledWarning' })} />}
            <SegmentedToggle
              label={formatMessage({ id: 'components.createHolidaysForm.holidayType' })}
              options={lengthOptions}
              value={values.length}
              onChange={(next) => setDates({ length: next as HolidayLength })}
              disabled={locks.whole || locks.dates}
              fitNarrowScreens
            />
            {datePicker('startDate', formatMessage({ id: multiDay ? 'users.startDate' : 'components.leaveForm.date' }), FIRST_PICKABLE_DAY)}
            {multiDay && datePicker('endDate', formatMessage({ id: 'users.endDate' }), values.startDate ? dayjs(values.startDate) : FIRST_PICKABLE_DAY)}
          </>
        )}
        <Divider />
        {section(
          formatMessage({ id: 'holidaysTab.repeatYearly' }),
          formatMessage({ id: 'holidaysTab.form.repeatDescription' }),
          <>
            <SegmentedToggle
              label={formatMessage({ id: 'holidaysTab.repeatYearly' })}
              options={yesNo}
              value={values.recurring}
              onChange={(next) => set({ recurring: next === true })}
              // Off is the only way a locked repeat can move, and once off it stays off.
              disabled={locks.whole || (locks.repeatOn && !values.recurring)}
            />
            {showRepeatDetails && (
              <>
                <SelectField
                  label={formatMessage({ id: 'components.createHolidaysForm.recurringType' })}
                  size="large"
                  searchable={false}
                  value={values.recurringType}
                  onChange={(next) => setDates({ recurringType: next as RecurringType })}
                  options={[
                    { value: 'EXACT_DAY', label: formatMessage({ id: 'components.createHolidaysForm.recurringTypeExactDay' }) },
                    { value: 'SPECIFIC_DAY', label: formatMessage({ id: 'components.createHolidaysForm.recurringTypeSpecificDay' }) },
                  ]}
                />
                {dynamic && (
                  <FieldGroup label={formatMessage({ id: 'components.createHolidaysForm.recurringDate' })}>
                    {daySetPart(0, toOptions(calendarHelper.monthWeeks), 'components.notificationForm.week')}
                    {daySetPart(1, toOptions(calendarHelper.days), 'components.notificationForm.day')}
                    {daySetPart(2, toOptions(calendarHelper.months), 'components.notificationForm.month')}
                  </FieldGroup>
                )}
                {dynamic && multiDay && (
                  <TextField
                    variant="number"
                    label={formatMessage({ id: 'components.tables.duration' })}
                    size="large"
                    unit={formatMessage({ id: 'app.days' })}
                    min={MIN_SPAN_DAYS}
                    value={values.duration}
                    onChange={(next) => set({ duration: next === null ? 0 : Number(next) })}
                    error={error('duration')}
                    style={{ width: 160 }}
                  />
                )}
                <SegmentedToggle
                  label={formatMessage({ id: 'components.createHolidaysForm.transferIfFallsOnNonWorkingDay' })}
                  options={yesNo}
                  value={values.transfer}
                  onChange={(next) => set({ transfer: next === true })}
                />
                {summary && <Text tone="secondary">{toText(summary)}</Text>}
              </>
            )}
          </>
        )}
        <Divider />
        <Stack gap="base" align="flex-end">
          {locks.whole && <Banner tone="warning" message={formatMessage({ id: existing ? 'components.createHolidaysForm.holidayCustomRecurringWarning' : 'error.generic' })} />}
          <Stack direction="horizontal" gap="sm" justify="flex-end">
            <Button variant="outline" disabled={false} onClick={backToHolidays}>
              {formatMessage({ id: 'app.cancel' })}
            </Button>
            <Button variant="primary" htmlType="submit" loading={btnLoading}>
              {formatMessage({ id: existingHolidayId ? 'app.save' : 'components.createHolidaysForm.title' })}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </div>
  )
}

export default LocationHolidayForm
